import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { formatGBP } from '../lib/currency';
import { booking, PARCEL_CATEGORIES, PARCEL_SIZES } from '../api/booking';
import { getCustomerSession } from '../lib/customerSession';
import { refusalMessage } from '../api/errors';
import { useRegion } from '../lib/useHomeCountry';
import { phoneShape } from '../lib/phoneShape';
import './bookRide.css';
import './pePayment.css';

/**
 * Whose phone number a booking was refused for, from the refusal's fields:
 * the client's own (the pickup's contact, and the door's when no recipient
 * is named) or the recipient's. Null for any other refusal.
 */
function refusedPhone(error) {
  const fields = Object.keys(error?.errors ?? {});
  if (fields.some((f) => f.startsWith('pickup') && f.includes('phone'))) return 'mine';
  if (fields.some((f) => f.startsWith('dropoff') && f.includes('phone'))) return 'theirs';

  return null;
}

function BackArrow() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15.5 18.5L8.5 12l7-7.5"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconCash() {
  return (
    <svg viewBox="0 0 32 32" width="26" height="26" aria-hidden>
      <rect x="3" y="5" width="20" height="12" rx="1" fill="#ec6c23" transform="rotate(-8 16 12)" />
      <rect
        x="5"
        y="10"
        width="20"
        height="12"
        rx="1"
        fill="#07408f"
        transform="rotate(4 16 16)"
        opacity="0.95"
      />
      <rect
        x="6"
        y="14"
        width="20"
        height="12"
        rx="1"
        fill="white"
        transform="rotate(-2 16 20)"
        stroke="#e0e0e0"
        strokeWidth="0.5"
      />
    </svg>
  );
}

function IconBike() {
  return (
    <svg viewBox="0 0 32 32" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.35" aria-hidden>
      <circle cx="8" cy="22" r="3.2" fill="none" />
      <circle cx="22" cy="22" r="3.2" fill="none" />
      <path
        d="M8 9h3l2.2 4.3L20.5 7H14M9.2 10.5L8 22M19.5 12.3L22 22M12.2 13.3L16.3 19H10"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

function formatPe(n) {
  return formatGBP(n);
}


/** Refusals that mean the price no longer holds: a new one is asked for, never re-priced silently. */
const PRICE_GONE = ['QUOTE_EXPIRED', 'PRICE_CHANGED', 'PINS_CHANGED', 'QUOTE_NOT_FOUND', 'QUOTE_USED'];

/**
 * The price and the booking for a client signed in on InGo's API (the mobile
 * plan's M5, 30 Sep 2026): the price is InGo's, quoted on the road between
 * the two pins the client chose and held for a quarter of an hour; there is no
 * offer to raise, and the booking sends the quote it was given. Cash on
 * delivery until InGo takes payments. The old screen below (a price worked
 * out in the phone, a bid) stays for the old accounts.
 */
function ApiPriceEstimate() {
  const navigate = useNavigate();
  // After a reload the state is null, not undefined: `= {}` would not replace it.
  const locationState = useLocation().state;
  const navState = useMemo(() => locationState || {}, [locationState]);
  const [quote, setQuote] = useState(null);
  const [status, setStatus] = useState('pricing');
  const [error, setError] = useState('');
  // A number the booking was refused for, corrected here rather than on the
  // steps before (Rio, 5 Oct 2026: "i tried to book for $20.61 but it does
  // not go through"; the page said only "Enter a valid phone number").
  const region = useRegion();
  const [phoneFix, setPhoneFix] = useState(null);
  const [fixProblem, setFixProblem] = useState('');
  const [attempt, setAttempt] = useState(0);
  const pickup = navState.pickupPin || null;
  const dropoff = navState.dropoffPin || null;
  const pkg = navState.package || {};
  const drops = (Array.isArray(navState.stops) ? navState.stops : []).map((s) => String(s?.value ?? '').trim()).filter(Boolean);
  const oneDrop = drops.length <= 1;
  // The vehicle type the client chose on the parcel screen, by its own service (`api/vehicles.js`).
  const wantedService = pkg.serviceType || '';

  useEffect(() => {
    if (!pickup || !dropoff) {
      navigate('/request-delivery', { replace: true });
      return undefined;
    }
    if (!oneDrop) {
      setStatus('failed');
      setError('A delivery goes to one drop-off for now. Go back and keep one.');
      return undefined;
    }
    let cancelled = false;
    setStatus('pricing');
    (async () => {
      try {
        const services = await booking.services();
        // Never another vehicle than the one chosen: the office may have switched it off since.
        const service = services.find((s) => s.key === wantedService);
        if (!service) {
          if (cancelled) return;
          setError(
            services.length === 0
              ? 'No delivery can be booked right now. Try again later.'
              : 'That vehicle cannot be booked right now. Go back and choose another.',
          );
          setStatus('failed');
          return;
        }
        const priced = await booking.quote(service.key, pickup, dropoff);
        if (cancelled) return;
        setQuote({ ...priced, label: service.label });
        setStatus('ready');
      } catch (e) {
        if (cancelled) return;
        setError(refusalMessage(e));
        setStatus('failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [pickup, dropoff, oneDrop, wantedService, attempt, navigate]);

  const book = async (e) => {
    e.preventDefault();
    if (!quote || status !== 'ready') return;
    if (phoneFix) {
      const shape = phoneShape(phoneFix.value, region);
      if (!shape.ok) {
        setFixProblem(shape.message);
        return;
      }
    }
    // The client who books is the sender: the rider's number to call at the
    // pickup, and at the door when no recipient is named on the package form.
    const me = getCustomerSession();
    const recipient = pkg.recipientPhone || dropoff.contactPhone || '';
    const mine = phoneFix?.field === 'mine' ? phoneFix.value.trim() : pickup.contactPhone || me?.phone || '';
    const theirs = phoneFix?.field === 'theirs' ? phoneFix.value.trim() : recipient || mine;
    setStatus('booking');
    setError('');
    try {
      const weight = parseFloat(String(pkg.weight || '').replace(',', '.'));
      const delivery = await booking.book({
        quote,
        pickup: { ...pickup, contactName: pickup.contactName || me?.full_name || '', contactPhone: mine },
        dropoff: {
          ...dropoff,
          contactName: pkg.recipientName || dropoff.contactName || me?.full_name || '',
          contactPhone: theirs,
        },
        parcel: {
          size: PARCEL_SIZES[pkg.size] || 'small',
          category: PARCEL_CATEGORIES[pkg.category] || 'other',
          categoryOther: pkg.category === 'Other' ? pkg.categoryOther || pkg.type || '' : '',
          weightKg: Number.isFinite(weight) && weight > 0 ? weight : null,
          notes: String(pkg.notes || '').trim(),
        },
      });
      navigate(`/live-tracking?delivery=${encodeURIComponent(delivery.id)}`, { replace: true });
    } catch (e2) {
      const whose = refusedPhone(e2);
      if (whose) {
        setPhoneFix({ field: whose, value: whose === 'mine' ? mine : theirs });
        setFixProblem('');
        setError(
          whose === 'mine'
            ? "Your phone number can't be called. Correct it below for this delivery, then book again."
            : "The recipient's phone number can't be called. Correct it below, then book again.",
        );
        setStatus('ready');
        return;
      }
      setError(refusalMessage(e2));
      if (PRICE_GONE.includes(e2?.code)) {
        // The price went: a new one is asked for at once, and shown.
        setQuote(null);
        setAttempt((n) => n + 1);
        return;
      }
      setStatus('ready');
    }
  };

  const total = quote ? formatPe(quote.total_minor / 100) : '…';
  const line = (minor) => (quote ? formatPe(minor / 100) : '…');
  const minutes = quote ? Math.max(1, Math.round(quote.duration_seconds / 60)) : null;
  const heldUntil = quote ? new Date(quote.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <form className="br-page br-page--form" onSubmit={book}>
      <header className="br-nav br-nav--stacked">
        <Link to="/package-details" className="br-nav__back" state={navState} aria-label="Back to package details">
          <BackArrow />
        </Link>
        <div className="br-nav__center">
          <h1 className="br-nav__title">Price</h1>
          <p className="br-nav__step">Step 3 of 3</p>
        </div>
        <span className="br-nav__spacer" aria-hidden />
      </header>

      <div className="br-scroll br-scroll--form">
        {error ? (
          <p className="br-pe-distance" role="alert" style={{ color: 'var(--ingo-danger, #b91c1c)' }}>
            <span className="br-pe-distance__val">{error}</span>
          </p>
        ) : null}

        {phoneFix ? (
          <section className="br-pd-card" aria-label="Correct the phone number">
            <h2 className="br-pd-heading">{phoneFix.field === 'mine' ? 'Your phone number' : "The recipient's phone number"}</h2>
            <div className="br-pd-field">
              <input
                className="br-pd-input"
                type="tel"
                inputMode="tel"
                value={phoneFix.value}
                onChange={(e) => {
                  setPhoneFix({ ...phoneFix, value: e.target.value });
                  setFixProblem('');
                }}
                placeholder={region?.home?.phoneExample ? `e.g. 0${region.home.phoneExample}` : ''}
                autoComplete="off"
                maxLength={32}
                aria-label={phoneFix.field === 'mine' ? 'Your phone' : "Recipient's phone"}
                aria-invalid={fixProblem ? true : undefined}
              />
            </div>
            {fixProblem ? (
              <p className="br-pd-error" role="alert">
                {fixProblem}
              </p>
            ) : null}
          </section>
        ) : null}

        <section className="br-pd-card br-pe-route" aria-label="Route summary">
          <h2 className="br-pd-heading">Route</h2>
          <div className="br-loc-list br-pe-loc" role="list">
            <div className="br-loc-row br-pe-loc__row" role="listitem">
              <span className="br-loc-row__dot br-loc-row__dot--pickup" aria-hidden />
              <div className="br-loc-row__main">
                <span className="br-loc-row__label">Pickup</span>
                <p className="br-pe-loc__addr">{pickup?.address || '—'}</p>
              </div>
            </div>
            <div className="br-loc-row br-pe-loc__row" role="listitem">
              <span className="br-loc-row__dot br-loc-row__dot--drop" aria-hidden />
              <div className="br-loc-row__main">
                <span className="br-loc-row__label">Drop-off</span>
                <p className="br-pe-loc__addr">{dropoff?.address || '—'}</p>
              </div>
            </div>
          </div>
          <p className="br-pe-distance">
            <span className="br-pe-distance__lab">Distance on the road</span>
            <span className="br-pe-distance__val">{quote ? `${quote.distance_km.toFixed(1)} km` : '…'}</span>
          </p>
          <p className="br-pe-distance">
            <span className="br-pe-distance__lab">Est. duration</span>
            <span className="br-pe-distance__val">{minutes ? `${minutes} min` : '…'}</span>
          </p>
        </section>

        <section className="br-pd-card" aria-label="Delivery price">
          <h2 className="br-pd-heading">Delivery</h2>
          <div className="br-pe-service" role="group" aria-label={`Delivery total ${total}`}>
            <span className="br-pe-service__icon" aria-hidden>
              <IconBike />
            </span>
            <div className="br-pe-service__body">
              <p className="br-pe-service__title">{quote?.label || 'Delivery'}</p>
              <p className="br-pe-service__sub">{quote ? `This price is held until ${heldUntil}` : 'Working out the price on the road…'}</p>
            </div>
            <span className="br-pe-service__price">{total}</span>
          </div>
        </section>

        <section className="br-pd-card br-pe-pay-card" aria-label="Payment method">
          <h2 className="br-pd-heading">Payment</h2>
          <div className="pay-list br-pe-pay-list" role="radiogroup" aria-label="Payment method">
            <label className="pay-row pay-row--on" htmlFor="pe-api-pay-cod">
              <span className="pay-row__icon" aria-hidden>
                <IconCash />
              </span>
              <span className="pay-row__body">
                <span className="pay-row__label">Cash on delivery</span>
                <span className="pay-row__sub">Pay the rider when your parcel arrives</span>
              </span>
              <input type="radio" id="pe-api-pay-cod" name="pe-api-payment" className="pay-row__radio" checked readOnly />
            </label>
          </div>
        </section>

        <section className="br-pd-card br-pe-breakdown" aria-label="Price breakdown">
          <h2 className="br-pd-heading">Price breakdown</h2>
          <div className="br-pe-break__row">
            <span>Base fare</span>
            <span>{line(quote?.lines?.base_fare_minor)}</span>
          </div>
          <div className="br-pe-break__row">
            <span>Distance fee</span>
            <span>{line(quote?.lines?.distance_fee_minor)}</span>
          </div>
          <div className="br-pe-break__row">
            <span>Time fee</span>
            <span>{line(quote?.lines?.time_fee_minor)}</span>
          </div>
          <div className="br-pe-break__row">
            <span>Service fee</span>
            <span>{line(quote?.lines?.service_fee_minor)}</span>
          </div>
          <hr className="br-pe-break__hr" />
          <div className="br-pe-break__row br-pe-break__row--total">
            <span>Total</span>
            <span>{total}</span>
          </div>
        </section>
      </div>

      <div className="br-footer">
        {status === 'failed' && oneDrop ? (
          <button type="button" className="br-confirm" onClick={() => setAttempt((n) => n + 1)}>
            Get the price again
          </button>
        ) : (
          <button type="submit" className="br-confirm" disabled={status !== 'ready'}>
            {status === 'booking' ? 'Booking…' : quote ? `Book for ${total}` : 'Pricing…'}
          </button>
        )}
      </div>
    </form>
  );
}

/** InGo's clients get InGo's price; the old accounts the old screen. */
export default function PriceEstimatePage() {
  return <ApiPriceEstimate />;
}
